(function () {
  /**
   * @param {Element} item
   * @returns {boolean}
   */
  function isSoldOut(item) {
    return !!item.querySelector('.vibee-product-grid__atc-btn--soldout');
  }

  /**
   * @param {HTMLElement | null} grid
   */
  function vibeeDemoteSoldOut(grid) {
    if (!grid) return;
    const items = Array.from(grid.children);
    const inStock = items.filter((i) => !isSoldOut(i));
    const sold = items.filter((i) => isSoldOut(i));
    if (sold.length === 0) return;

    const desired = [...inStock, ...sold];
    let changed = false;
    for (let k = 0; k < items.length; k++) {
      if (items[k] !== desired[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;

    const frag = document.createDocumentFragment();
    desired.forEach((el) => frag.appendChild(el));
    grid.appendChild(frag);
  }

  function isMobileFilters() {
    return window.matchMedia('(max-width: 48rem)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {HTMLElement} root
   */
  function destroyInfiniteScroll(root) {
    if (typeof root._vibeeInfiniteTeardown === 'function') {
      root._vibeeInfiniteTeardown();
    }
  }

  /**
   * @param {HTMLElement} sentinel
   * @returns {number}
   */
  function getSentinelDocTop(sentinel) {
    return sentinel.getBoundingClientRect().top + window.scrollY;
  }

  /**
   * @param {HTMLElement} root
   */
  function initInfiniteScroll(root) {
    destroyInfiniteScroll(root);

    const grid = root.querySelector('.vibee-collection__grid');
    const loadMoreWrap = root.querySelector('.vibee-collection__load-more');
    const loadMoreLink = root.querySelector('[data-vibee-load-more-link]');
    const sentinel = root.querySelector('[data-vibee-infinite-sentinel]');
    const loadingEl = root.querySelector('[data-vibee-infinite-loading]');
    const statusWrap = root.querySelector('.vibee-collection__infinite-status');
    const announce = root.querySelector('[data-vibee-infinite-announce]');

    if (!grid || !loadMoreLink?.href || !sentinel) {
      return;
    }

    let nextUrl = loadMoreLink.href;
    let inFlight = false;
    let scrollTicking = false;
    let lastY = window.scrollY;
    /** @type {ReturnType<typeof setInterval> | null} */
    let scrollPollId = null;
    const prefetchMargin = 600;

    function readNextUrl(doc) {
      const link = doc.querySelector('[data-vibee-load-more-link]');
      return link?.href || null;
    }

    function announceLoaded(count) {
      if (!announce || count <= 0) return;
      const template = announce.dataset.infiniteMsg || 'COUNT';
      announce.textContent = template.replace(/COUNT/g, String(count));
    }

    function detachScrollListeners() {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, true);
      if (scrollPollId !== null) {
        clearInterval(scrollPollId);
        scrollPollId = null;
      }
    }

    function removeInfiniteChrome() {
      detachScrollListeners();
      sentinel.remove();
      statusWrap?.remove();
      loadMoreWrap?.remove();
    }

    function shouldLoadMore() {
      const sentinelTop = getSentinelDocTop(sentinel);
      return window.scrollY + window.innerHeight > sentinelTop - prefetchMargin;
    }

    async function loadNextPage() {
      if (inFlight || !nextUrl) return;
      inFlight = true;
      loadingEl?.classList.add('is-visible');
      loadingEl?.setAttribute('aria-hidden', 'false');

      const fetchUrl = nextUrl;

      try {
        const response = await fetch(fetchUrl, {credentials: 'same-origin'});
        if (!response.ok) throw new Error('fetch failed');

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = doc.querySelectorAll(
          '.vibee-collection__grid .vibee-collection__item',
        );

        items.forEach((item) => {
          grid.appendChild(item.cloneNode(true));
        });

        vibeeDemoteSoldOut(grid);
        announceLoaded(items.length);

        if (window.history && typeof window.history.replaceState === 'function') {
          window.history.replaceState(null, '', fetchUrl);
        }

        const upcoming = readNextUrl(doc);
        if (upcoming) {
          nextUrl = upcoming;
          loadMoreLink.href = upcoming;
        } else {
          nextUrl = '';
          removeInfiniteChrome();
        }
      } catch (_err) {
        detachScrollListeners();
      } finally {
        inFlight = false;
        loadingEl?.classList.remove('is-visible');
        loadingEl?.setAttribute('aria-hidden', 'true');
      }
    }

    /** @param {Event} event */
    function onLoadMoreClick(event) {
      event.preventDefault();
      loadNextPage();
    }

    function onScroll() {
      if (scrollTicking || inFlight || !nextUrl) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        if (shouldLoadMore()) {
          loadNextPage();
        }
        scrollTicking = false;
      });
    }

    loadMoreLink.addEventListener('click', onLoadMoreClick);
    window.addEventListener('scroll', onScroll, {passive: true});
    document.addEventListener('scroll', onScroll, {passive: true, capture: true});
    scrollPollId = setInterval(() => {
      if (lastY !== window.scrollY) {
        lastY = window.scrollY;
        onScroll();
      }
    }, 250);
    onScroll();

    root._vibeeInfiniteTeardown = () => {
      detachScrollListeners();
      loadMoreLink.removeEventListener('click', onLoadMoreClick);
      root._vibeeInfiniteTeardown = null;
    };
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getGrid(root) {
    return root.querySelector('[data-vibee-plp-grid]');
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  function getGridHost(root) {
    return root.querySelector('[data-vibee-plp-grid-host]');
  }

  /**
   * @param {HTMLElement} grid
   */
  function revealGrid(grid) {
    if (!grid || prefersReducedMotion()) return;

    grid.classList.remove('is-filter-fading', 'is-filter-revealed');
    grid.classList.add('is-filter-revealing');
    requestAnimationFrame(() => {
      grid.classList.add('is-filter-revealed');
    });

    window.setTimeout(() => {
      grid.classList.remove('is-filter-revealing', 'is-filter-revealed');
    }, 320);
  }

  /**
   * @param {HTMLElement} root
   * @param {Document} doc
   */
  function syncFilterUi(root, doc) {
    const newRoot = doc.querySelector('[data-vibee-plp]');
    if (!newRoot) return;

    const drawerBody = root.querySelector('[data-vibee-plp-drawer] .vibee-plp__drawer-body');
    const newDrawerBody = newRoot.querySelector('[data-vibee-plp-drawer] .vibee-plp__drawer-body');
    if (drawerBody && newDrawerBody) {
      drawerBody.innerHTML = newDrawerBody.innerHTML;
    }

    const count = root.querySelector('.vibee-plp__count, .vibee-brand-collection-main__count');
    const newCount = newRoot.querySelector('.vibee-plp__count, .vibee-brand-collection-main__count');
    if (count && newCount) {
      count.textContent = newCount.textContent;
    }

    const chips = root.querySelector('.vibee-plp__chips');
    const newChips = newRoot.querySelector('.vibee-plp__chips');
    if (chips && newChips) {
      chips.innerHTML = newChips.innerHTML;
    }

    const filterBtn = root.querySelector('.vibee-plp__filter-btn, .vibee-plp__toolbar-btn--filter');
    const newFilterBtn = newRoot.querySelector('.vibee-plp__filter-btn, .vibee-plp__toolbar-btn--filter');
    if (filterBtn && newFilterBtn) {
      filterBtn.innerHTML = newFilterBtn.innerHTML;
    }

    const clearLink = root.querySelector('.vibee-plp__filters-clear');
    const newClearLink = newRoot.querySelector('.vibee-plp__filters-clear');
    if (clearLink && newClearLink) {
      clearLink.href = newClearLink.href;
      clearLink.hidden = false;
    } else if (clearLink && !newClearLink) {
      clearLink.remove();
    } else if (!clearLink && newClearLink) {
      const head = root.querySelector('.vibee-plp__drawer-head');
      const closeBtn = head?.querySelector('.vibee-plp__drawer-close');
      if (head && closeBtn) {
        head.insertBefore(newClearLink.cloneNode(true), closeBtn);
      }
    }
  }

  /**
   * @param {HTMLElement} root
   * @param {string} url
   * @returns {Promise<boolean>}
   */
  async function applyFilterNavigation(root, url) {
    const grid = getGrid(root);
    const host = getGridHost(root);

    if (!grid || !host || !isMobileFilters() || prefersReducedMotion()) {
      window.location.assign(url);
      return false;
    }

    host.classList.add('is-filter-loading');
    grid.classList.add('is-filter-fading');

    await new Promise((resolve) => {
      window.setTimeout(resolve, 150);
    });

    try {
      const response = await fetch(url, {credentials: 'same-origin'});
      if (!response.ok) throw new Error('fetch failed');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-vibee-plp-grid]');
      const newEmpty = doc.querySelector(
        '.vibee-collection__empty, .vibee-brand-collection-main__empty',
      );

      if (!newGrid && newEmpty) {
        grid.innerHTML = '';
        let emptyEl = host.querySelector(
          '.vibee-collection__empty, .vibee-brand-collection-main__empty',
        );
        if (!emptyEl) {
          emptyEl = newEmpty.cloneNode(true);
          host.appendChild(emptyEl);
        } else {
          emptyEl.textContent = newEmpty.textContent;
          emptyEl.hidden = false;
        }
      } else if (!newGrid) {
        throw new Error('missing grid');
      } else {
        grid.innerHTML = newGrid.innerHTML;
        host
          .querySelector('.vibee-collection__empty, .vibee-brand-collection-main__empty')
          ?.remove();
      }
      vibeeDemoteSoldOut(grid);
      syncFilterUi(root, doc);

      if (window.history && typeof window.history.replaceState === 'function') {
        window.history.replaceState(null, '', url);
      }

      revealGrid(grid);
      destroyInfiniteScroll(root);
      initInfiniteScroll(root);
      return true;
    } catch (_err) {
      window.location.assign(url);
      return false;
    } finally {
      host.classList.remove('is-filter-loading');
      grid.classList.remove('is-filter-fading');
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initPlp(root) {
    if (!root || root.dataset.vibeePlpInit === 'true') return;
    root.dataset.vibeePlpInit = 'true';

    const overlay = root.querySelector('[data-vibee-plp-overlay]');
    const drawer = root.querySelector('[data-vibee-plp-drawer]');
    const sortSelect = root.querySelector('[data-vibee-plp-sort]');
    const sortOpenBtn = root.querySelector('[data-vibee-plp-sort-open]');
    const sortSheet = root.querySelector('[data-vibee-plp-sort-sheet]');
    const sortOptionsList = root.querySelector('[data-vibee-plp-sort-options]');

    if (root.classList.contains('vibee-collection')) {
      vibeeDemoteSoldOut(root.querySelector('.vibee-collection__grid'));
    }

    function setFilterExpanded(open) {
      root.querySelectorAll('[data-vibee-plp-filter-open]').forEach((btn) => {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    function scrollToFacet(facetKey) {
      if (!drawer || !facetKey) return;
      const group =
        drawer.querySelector(`[data-facet="${facetKey}"]`) ||
        drawer.querySelector(`[data-facet-label="${facetKey}"]`);
      if (!group) return;
      group.open = true;
      group.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }

    function setDrawer(open, facetKey) {
      const isOpen = Boolean(open);
      overlay?.classList.toggle('is-open', isOpen);
      drawer?.classList.toggle('is-open', isOpen);
      setFilterExpanded(isOpen);
      document.documentElement.classList.toggle('vibee-plp-drawer-open', isOpen);
      if (isOpen && facetKey) {
        requestAnimationFrame(() => scrollToFacet(facetKey));
      }
    }

    function setSortSheet(open) {
      if (!sortSheet) return;
      const isOpen = Boolean(open);
      sortSheet.classList.toggle('is-open', isOpen);
      sortSheet.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      sortOpenBtn?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      document.documentElement.classList.toggle('vibee-plp-sort-open', isOpen);
    }

    function buildSortSheet() {
      if (!sortSelect || !sortOptionsList || sortOptionsList.childElementCount) return;
      Array.from(sortSelect.options).forEach((option) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vibee-plp__sort-sheet-option';
        btn.textContent = option.textContent || '';
        btn.dataset.sortValue = option.value;
        if (option.selected) btn.classList.add('is-selected');
        btn.addEventListener('click', () => {
          sortSelect.value = option.value;
          sortSelect.dispatchEvent(new Event('change', {bubbles: true}));
          setSortSheet(false);
        });
        li.appendChild(btn);
        sortOptionsList.appendChild(li);
      });
    }

    buildSortSheet();

    root.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const openBtn = target.closest('[data-vibee-plp-filter-open]');
      if (openBtn && root.contains(openBtn)) {
        const facetKey = openBtn.getAttribute('data-facet') || '';
        setDrawer(true, facetKey);
        return;
      }

      if (target.closest('[data-vibee-plp-filter-close]')) {
        setDrawer(false);
        return;
      }

      if (
        overlay?.classList.contains('is-open') &&
        (target === overlay || target.closest('[data-vibee-plp-overlay]') === overlay)
      ) {
        setDrawer(false);
        return;
      }

      if (target.closest('[data-vibee-plp-sort-open]')) {
        setSortSheet(true);
        return;
      }

      if (target.closest('[data-vibee-plp-sort-close]')) {
        setSortSheet(false);
        return;
      }

      const filterLink = target.closest('.vibee-plp__filter-link, .vibee-plp__filters-clear');
      if (
        filterLink instanceof HTMLAnchorElement &&
        root.contains(filterLink) &&
        isMobileFilters()
      ) {
        event.preventDefault();
        applyFilterNavigation(root, filterLink.href).then((ok) => {
          if (ok) setDrawer(false);
        });
      }
    });

    root.querySelectorAll('.vibee-plp__facet-form').forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (!isMobileFilters()) return;
        event.preventDefault();
        const action = form.getAttribute('action') || window.location.pathname;
        const url = new URL(action, window.location.origin);
        const fd = new FormData(form);
        fd.forEach((value, key) => {
          if (value !== '') url.searchParams.set(key, String(value));
        });
        applyFilterNavigation(root, url.toString()).then((ok) => {
          if (ok) setDrawer(false);
        });
      });
    });

    sortSelect?.addEventListener('change', (event) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      window.location.assign(url.toString());
    });

    initInfiniteScroll(root);
  }

  function bootPlp() {
    document.querySelectorAll('[data-vibee-plp]').forEach(initPlp);
  }

  function scheduleBootPlp() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootPlp, {once: true});
    } else {
      bootPlp();
    }
  }

  scheduleBootPlp();
  document.addEventListener('shopify:section:load', bootPlp);

  window.vibeePlpReinitInfinite = function (rootEl) {
    const root =
      rootEl instanceof HTMLElement
        ? rootEl
        : document.querySelector('[data-vibee-plp]');
    if (root) initInfiniteScroll(root);
  };
})();
